import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import Lightbox from '../components/Lightbox';

// ── Maternity ──
import ma01 from '../imagesForCategory/Maternity/Ma 01.jpg';
import ma02 from '../imagesForCategory/Maternity/Ma 02.jpg';
import ma03 from '../imagesForCategory/Maternity/Ma 03.jpg';
import ma04 from '../imagesForCategory/Maternity/Ma 04.jpg';
import ma05 from '../imagesForCategory/Maternity/Ma 05.jpg';
import ma06 from '../imagesForCategory/Maternity/Ma 06.JPG';
import ma07 from '../imagesForCategory/Maternity/Ma 07.jpg';
import ma08 from '../imagesForCategory/Maternity/Ma 08.jpg';

// ── 1st Birthday ──
import b01 from '../imagesForCategory/Birthday/B 01.jpg';
import b02 from '../imagesForCategory/Birthday/B 02.jpg';
import b03 from '../imagesForCategory/Birthday/B 03.jpg';
import b04 from '../imagesForCategory/Birthday/B 04.jpg';

// ── Pre-Birthday ──
import preb01 from '../imagesForCategory/Prebirthday/Preb 01.jpg';
import preb02 from '../imagesForCategory/Prebirthday/Preb 02.jpg';
import preb03 from '../imagesForCategory/Prebirthday/Preb 03.jpg';
import preb04 from '../imagesForCategory/Prebirthday/Preb 04.jpg';
import preb05 from '../imagesForCategory/Prebirthday/Preb 05.jpg';
import preb06 from '../imagesForCategory/Prebirthday/Preb 06.jpg';
import preb07 from '../imagesForCategory/Prebirthday/Preb 07.jpg';
import preb08 from '../imagesForCategory/Prebirthday/Preb 08.jpg';
import preb09 from '../imagesForCategory/Prebirthday/Preb 09.jpg';
import preb10 from '../imagesForCategory/Prebirthday/Preb 10.jpg';
import preb11 from '../imagesForCategory/Prebirthday/Preb 11.jpg';
import preb12 from '../imagesForCategory/Prebirthday/Preb 12.jpg';
import preb13 from '../imagesForCategory/Prebirthday/Preb 13.jpg';
import preb14 from '../imagesForCategory/Prebirthday/Preb 14.jpg';

const categories = [
  {
    id: 'newborn',
    title: 'Newborn',
    subtitle: 'Tiny Miracles, Forever Treasured',
    description:
      'The arrival of a newborn is one of life\'s most precious miracles. We specialize in capturing those fleeting early days — the tiny fingers, peaceful slumbers, and tender family moments — with gentle, artistic photography that you\'ll treasure for a lifetime.',
    images: [preb01, preb02, preb03, preb04, preb05, preb06, preb07],
  },
  {
    id: '1st-birthday',
    title: '1st Birthday',
    subtitle: 'The First Milestone Celebration',
    description:
      'Your baby\'s first birthday is a landmark celebration filled with joy, laughter, and love. From smash cakes to family portraits, we capture every colorful, heartwarming detail of this once-in-a-lifetime milestone with cinematic perfection.',
    images: [b01, b02, b03, b04],
  },
  {
    id: 'pre-birthday',
    title: 'Pre-Birthday',
    subtitle: 'Growing Up, One Frame at a Time',
    description:
      'Pre-birthday shoots are a wonderful way to celebrate your child\'s journey before the big day. We create magical, themed photo sessions that capture your little one\'s personality, curiosity, and charm in beautifully crafted compositions.',
    images: [preb08, preb09, preb10, preb11, preb12, preb13, preb14],
  },
  {
    id: 'maternity',
    title: 'Maternity',
    subtitle: 'The Beauty of New Beginnings',
    description:
      'Maternity photography celebrates the beautiful journey of motherhood. We capture the glow, grace, and anticipation of this transformative time with elegant, artistic portraits that honor the bond between mother and child even before birth.',
    images: [ma01, ma02, ma03, ma04, ma05, ma06, ma07, ma08],
  },
];

// ─────────────────────────────────────────
// Image grid layout component
// ─────────────────────────────────────────
function ImageGrid({ images, title, catIdx, onOpen }) {
  if (images.length <= 3) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 stagger">
        {images.map((img, i) => (
          <div key={i} className="album-card scale-up aspect-[3/4]" onClick={() => onOpen(catIdx, i)}>
            <img src={img} alt={`${title} ${i + 1}`} loading="lazy" />
            <div className="overlay">
              <span className="text-cream text-xs font-montserrat tracking-[0.1em] uppercase">{title}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (images.length <= 5) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 stagger">
        {images.map((img, i) => (
          <div
            key={i}
            className={`album-card scale-up ${i === 0 ? 'md:col-span-2 md:row-span-2' : 'aspect-square'}`}
            onClick={() => onOpen(catIdx, i)}
          >
            <img src={img} alt={`${title} ${i + 1}`} loading="lazy" />
            <div className="overlay">
              <span className="text-cream text-xs font-montserrat tracking-[0.1em] uppercase">{title}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-12 gap-4">
        <div className="md:col-span-7 album-card scale-up aspect-[4/3] md:aspect-auto md:min-h-[480px]" onClick={() => onOpen(catIdx, 0)}>
          <img src={images[0]} alt={`${title} featured`} loading="lazy" />
          <div className="overlay">
            <div>
              <span className="text-cream text-xs font-montserrat tracking-[0.1em] uppercase block mb-1">{title}</span>
              <span className="text-cream/50 text-[10px] font-montserrat">1 of {images.length}</span>
            </div>
          </div>
        </div>
        <div className="md:col-span-5 grid grid-cols-2 gap-4">
          {images.slice(1, 5).map((img, i) => (
            <div key={i} className="album-card scale-up aspect-square" onClick={() => onOpen(catIdx, i + 1)}>
              <img src={img} alt={`${title} ${i + 2}`} loading="lazy" />
              <div className="overlay">
                <span className="text-cream/70 text-[10px] font-montserrat">{i + 2}/{images.length}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {images.length > 5 && (
        <div className="grid grid-cols-3 gap-4 stagger">
          {images.slice(5).map((img, i) => (
            <div key={i} className="album-card scale-up aspect-[4/3]" onClick={() => onOpen(catIdx, i + 5)}>
              <img src={img} alt={`${title} ${i + 6}`} loading="lazy" />
              <div className="overlay">
                <span className="text-cream/70 text-[10px] font-montserrat">{i + 6}/{images.length}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// Main Baby Shoot Page
// ─────────────────────────────────────────
export default function BabyShoot() {
  useScrollAnimation();
  const [activeCategory, setActiveCategory] = useState('newborn');
  const [lightboxData, setLightboxData] = useState({ catIdx: null, imgIdx: null });
  const stickyRef = useRef(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const sectionRefsMap = useRef({});

  const categoryIds = useMemo(() => categories.map((c) => c.id), []);

  // ── Active section tracking ──
  useEffect(() => {
    let ticking = false;

    const updateActive = () => {
      if (isScrollingRef.current) return;

      const stickyHeight = stickyRef.current ? stickyRef.current.offsetHeight : 50;
      const headerHeight = 70;
      const offset = stickyHeight + headerHeight + 20;

      let bestId = categoryIds[0];
      let bestDistance = Infinity;

      for (const id of categoryIds) {
        const el = sectionRefsMap.current[id] || document.getElementById(id);
        if (!el) continue;
        sectionRefsMap.current[id] = el;

        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top - offset);

        if (rect.top <= offset + 100 && distance < bestDistance) {
          bestDistance = distance;
          bestId = id;
        }
      }

      setActiveCategory((prev) => (prev !== bestId ? bestId : prev));
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateActive);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    requestAnimationFrame(updateActive);

    return () => window.removeEventListener('scroll', onScroll);
  }, [categoryIds]);

  // ── Scroll to section ──
  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;

    setActiveCategory(id);
    isScrollingRef.current = true;
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

    const stickyHeight = stickyRef.current ? stickyRef.current.offsetHeight : 50;
    const headerHeight = 70;
    const top = el.getBoundingClientRect().top + window.scrollY - stickyHeight - headerHeight;

    window.scrollTo({ top, behavior: 'smooth' });

    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 900);
  }, []);

  // ── Auto-scroll active pill into view ──
  useEffect(() => {
    const pill = document.querySelector('.cat-pill.active');
    if (pill) {
      pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeCategory]);

  // ── Lightbox ──
  const openLightbox = useCallback((catIdx, imgIdx) => {
    setLightboxData({ catIdx, imgIdx });
  }, []);
  const closeLightbox = useCallback(() => {
    setLightboxData({ catIdx: null, imgIdx: null });
  }, []);

  const currentCatImages = lightboxData.catIdx !== null ? categories[lightboxData.catIdx].images : [];

  return (
    <main>
      {/* ═══ STICKY CATEGORY NAV ═══ */}
      <div
        ref={stickyRef}
        className="sticky top-[60px] lg:top-[64px] z-50 bg-charcoal/98 backdrop-blur-lg border-y border-gold/8"
      >
        <div className="max-w-7xl mx-auto px-2 overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-0.5 py-2 min-w-max mx-auto w-fit">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => scrollToSection(cat.id)}
                className={`cat-pill ${activeCategory === cat.id ? 'active' : ''}`}
              >
                {cat.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ CATEGORY SECTIONS ═══ */}
      {categories.map((cat, catIdx) => (
        <section
          key={cat.id}
          id={cat.id}
          className={`py-20 md:py-28 ${catIdx % 2 === 0 ? 'bg-charcoal' : 'bg-charcoal-light'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`mb-12 md:mb-16 max-w-3xl ${catIdx % 2 === 0 ? 'mx-auto text-center' : ''}`}>
              <div className="fade-up">
                <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-montserrat mb-2">
                  {cat.subtitle}
                </p>
                <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-cream mb-4">
                  {cat.title}
                </h2>
                <div className={`mb-6 ${catIdx % 2 === 0 ? 'gold-line' : 'gold-line-left'}`} />
                <p className="text-cream/55 text-sm md:text-base leading-[1.8] font-light">
                  {cat.description}
                </p>
              </div>
            </div>

            <ImageGrid
              images={cat.images}
              title={cat.title}
              catIdx={catIdx}
              onOpen={openLightbox}
            />
          </div>

          {catIdx < categories.length - 1 && (
            <div className="section-divider mt-20 md:mt-28" />
          )}
        </section>
      ))}

      {/* ═══ CTA ═══ */}
      <section
        className="parallax-section py-28 md:py-36"
        style={{ backgroundImage: `url(${ma05})` }}
      >
        <div className="max-w-4xl mx-auto px-4 text-center fade-up">
          <p className="text-gold text-xs tracking-[0.3em] uppercase font-montserrat mb-3">Capture the Magic</p>
          <h2 className="font-playfair text-4xl md:text-5xl text-cream mb-6">
            Book a <span className="italic text-gold">Baby Shoot</span>
          </h2>
          <div className="gold-line mb-8" />
          <p className="text-cream/60 text-base md:text-lg mb-12 max-w-2xl mx-auto font-light">
            These moments pass so quickly. Let us preserve the joy, innocence, and wonder of your little one in timeless photographs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919703377033" className="btn-gold">Call +91-9703377033</a>
            <a href="https://wa.me/919703377033" target="_blank" rel="noopener noreferrer" className="btn-outline">WhatsApp Us</a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxData.imgIdx !== null && (
        <Lightbox
          image={currentCatImages[lightboxData.imgIdx]}
          onClose={closeLightbox}
          onPrev={lightboxData.imgIdx > 0 ? () => setLightboxData((d) => ({ ...d, imgIdx: d.imgIdx - 1 })) : null}
          onNext={lightboxData.imgIdx < currentCatImages.length - 1 ? () => setLightboxData((d) => ({ ...d, imgIdx: d.imgIdx + 1 })) : null}
        />
      )}
    </main>
  );
}
